import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import {
  CalculationStatus,
  DEFAULT_OTHER_SPEED_STEAMING_OPTION,
  INIT_MODES,
  IReportTabState,
  InitMode,
} from '@navstation-portal/reporting/data-model/vessel-report';
import {
  BALLAST_CODE_OPTIONS,
  MANEUVERING_TYPE_OPTIONS,
  OPERATING_CODE_OPTIONS,
  OperationalState,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { convertCodeToOption } from '@navstation-portal/reporting/util/form-rules';
import { LatLngModel, SelectOption } from '@navstation-portal/shared/data-model/common';
import { tvaTimezoneToSelectOption } from '@navstation-portal/shared/util/time';
import { VesselParticularsStore, VesselSpecStore } from '@navstation-portal/vessels/data-access/store';
import { signalStore, withComputed, withFeature, withState } from '@ngrx/signals';
import { initStateField } from '../../utils/store.utility';
import { withCalculatedValues } from '../with-calculated-values';
import { withCalculationReducer } from '../with-calculation-reducer';
import { withFormStoreReducer } from '../with-form-store-reducer';
import { collectOperationalCalculatedValues } from './operational-calculated-values';
import { operationalCalculationEvents, operationalEvents } from './operational-events';
import { buildOperationalVm } from './operational-vm-builder';
import { withOperationalEffects } from './with-operational-effects';
import { withPortCallPurposesReducer } from './with-port-call-purposes-reducer';

const NEW_REPORT_DEFAULT_BALLAST_CODE = BALLAST_CODE_OPTIONS[0].code as string;

/**
 * Turns a null into an unanswered field.
 *
 * A yes-or-no field holds three answers: the user has not answered, the answer
 * is no, and the answer is yes. A null from the API means the first, and it
 * reaches the form as a fourth shape that nothing else expects. A rule that
 * asks whether the field is unset cannot tell a null from the answer no, and a
 * rule that asks whether it is false cannot tell a null from unanswered, so one
 * of the two must read a null wrong.
 *
 * Previously in ECA is the field that needs this: the two ECA fields enable on
 * the answer, and the form-rule template asks about it. See the Enter ECA and
 * Exit ECA rules.
 */
const asAnswer = (value: boolean | null | undefined): boolean | undefined => value ?? undefined;

const createEmptyLatLng = () => ({
  latDegrees: undefined,
  latMinutes: undefined,
  latSeconds: undefined,
  latDirection: undefined,
  longDegrees: undefined,
  longMinutes: undefined,
  longSeconds: undefined,
  longDirection: undefined,
});

const initOperationalStateReducer = (initialData: IReportTabState, initMode: InitMode) => {
  const data = initialData as OperationalState;
  switch (initMode) {
    case INIT_MODES.CARRY_OVER:
      return initWithCarryOver(data);
    case INIT_MODES.IMPORT:
      return initWithExistingReport(data);
    case INIT_MODES.BLANK:
      return initBlank();
    default:
      return initBlank();
  }
};

const initBlank = () => {
  return {
    formState: {
      vesselDetails: {
        reportNumber: initStateField<number>(),
        voyageNumber: initStateField<string>(),
        cpSpeed: initStateField<number>(),
        cpFuel: initStateField<number>(),
        instructedSpeed: initStateField<number>(),
        instructedFoc: initStateField<number>(),
        otherSpeedSteaming: initStateField<SelectOption>(DEFAULT_OTHER_SPEED_STEAMING_OPTION),
      },
      miscellaneous: {
        captainName: initStateField<string>(),
        chiefEngineer: initStateField<string>(),
        cargoWeight: initStateField<number>(),
        operatingCode: initStateField<SelectOption>(),
        ballastCode: initStateField<SelectOption>(
          convertCodeToOption(NEW_REPORT_DEFAULT_BALLAST_CODE, BALLAST_CODE_OPTIONS)
        ),
        routingAdviceFollowed: initStateField<boolean>(),
      },
      cargoDetails: {
        cargoWeight: initStateField<number>(),
      },
      operatingConditions: {
        operationType: initStateField<SelectOption>(),
        cargoLocation: initStateField<SelectOption>(),
        tugsAllFast: initStateField<number>(),
        tugsLastLine: initStateField<number>(),
        draughtForward: initStateField<number>(),
        draughtAft: initStateField<number>(),
        trim: initStateField<number>(),
        recommendedTrim: initStateField<number>(),
        ballastWaterOnBoard: initStateField<number>(),
        ballastWaterIntake: initStateField<number>(),
        ballastWaterDischarged: initStateField<number>(),
        ballastWaterSampleDate: initStateField<Date>(),
        ballastWaterCountOfCells: initStateField<number>(),
      },
      reportTime: {
        reportFrom: initStateField<Date>(),
        reportTo: initStateField<Date>(),
        eosp: initStateField<Date>(),
        reportCreation: initStateField<Date>(),
        timezone: initStateField<SelectOption>(),
        previousTimezone: initStateField<SelectOption>(),
        reportPeriod: initStateField<number>(),
        timeSailed: initStateField<number>(),
      },
      portAndPosition: {
        portName: initStateField<string>(),
        portPosition: initStateField<LatLngModel>(),
      },
      stabilityConditions: {
        displacement: initStateField<number>(),
        gm: initStateField<number>(),
        shearForce: initStateField<number>(),
        bendingForce: initStateField<number>(),
        torsion: initStateField<number>(),
        gmReserve: initStateField<number>(),
        deadWeightReserve: initStateField<number>(),
        constantWeight: initStateField<number>(),
      },
      portOperationsTimestamp: {
        etd: initStateField<Date>(),
        norTendered: initStateField<Date>(),
        allFast: initStateField<Date>(),
        hosesConnected: initStateField<Date>(),
        commenceLoad: initStateField<Date>(),
        finishLoad: initStateField<Date>(),
        hosesDisconnected: initStateField<Date>(),
        lastLine: initStateField<Date>(),
        cargoDocsOnboard: initStateField<Date>(),
        startDischarging: initStateField<Date>(),
        endDischarging: initStateField<Date>(),
        completedTankInspection: initStateField<Date>(),
      },
      poolSpecificFields: {
        freshWaterDomestic: initStateField<number>(),
        freshWaterTankCleaning: initStateField<number>(),
        slopsResidualTanks: initStateField<number>(),
        slopsSlopTanks: initStateField<number>(),
        totalInterfaceOil: initStateField<number>(),
        totalInterfaceWater: initStateField<number>(),
        firstLine: initStateField<Date>(),
        gangwayDown: initStateField<Date>(),
        cargoDocsOnboard: initStateField<Date>(),
      },
      nextPort: {
        awaitingOrders: initStateField<boolean>(),
        nextPort: initStateField<string>(),
        remainingDistance: initStateField<number>(),
        remainingTime: initStateField<number>(),
        nextTimezone: initStateField<SelectOption>(),
        etaPilotStation: initStateField<Date>(),
      },
      otherDetails: {
        numPassengers: initStateField<number>(),
        numCrew: initStateField<number>(),
      },
      voyageItinerary: {
        maneuveringType: initStateField<SelectOption>(),
        awaitingOrders: initStateField<boolean>(),
        arrivingAtEosp: initStateField<boolean>(),
        nextPort: initStateField<string>(),
        eta: initStateField<Date>(),
        etaTimezone: initStateField<SelectOption>(),
        previousEta: initStateField<Date>(),
        cosp: initStateField<Date>(),
        previouslyInEca: initStateField<boolean>(),
        enterEca: initStateField<Date>(),
        exitEca: initStateField<Date>(),
        timeInEca: initStateField<number>(),
        inEca: initStateField<boolean>(),
      },
      portCallReasons: {
        purposesOfCall: initStateField<string[]>([]),
      },
    },
    calculations: {} as Record<string, CalculationStatus>,
  };
};

const initWithExistingReport = (data: OperationalState) => {
  return {
    formState: {
      vesselDetails: {
        reportNumber: initStateField(data?.vesselDetails?.reportNumber ?? 0),
        voyageNumber: initStateField(data?.vesselDetails?.voyageNumber),
        cpSpeed: initStateField(data?.vesselDetails?.cpSpeed),
        cpFuel: initStateField(data?.vesselDetails?.cpFuel),
        instructedSpeed: initStateField(data?.vesselDetails?.instructedSpeed),
        instructedFoc: initStateField(data?.vesselDetails?.instructedFoc),
        otherSpeedSteaming: initStateField(
          data?.vesselDetails?.otherSpeedSteaming ?? DEFAULT_OTHER_SPEED_STEAMING_OPTION
        ),
      },
      miscellaneous: {
        captainName: initStateField(data.miscellaneous.captainName),
        chiefEngineer: initStateField(data.miscellaneous.chiefEngineer),
        cargoWeight: initStateField(data.miscellaneous.cargoWeight),
        operatingCode: initStateField(convertCodeToOption(data.miscellaneous.operatingCode, OPERATING_CODE_OPTIONS)),
        ballastCode: initStateField(convertCodeToOption(data.miscellaneous.ballastCode, BALLAST_CODE_OPTIONS)),
        routingAdviceFollowed: initStateField(data.miscellaneous.routingAdviceFollowed),
      },
      cargoDetails: {
        cargoWeight: initStateField(data.cargoDetails?.cargoWeight),
      },
      operatingConditions: {
        operationType: initStateField(data.operatingConditions.operationType),
        cargoLocation: initStateField(data.operatingConditions.cargoLocation),
        tugsAllFast: initStateField(data.operatingConditions.tugsAllFast),
        tugsLastLine: initStateField(data.operatingConditions.tugsLastLine),
        draughtForward: initStateField(data.operatingConditions.draughtForward),
        draughtAft: initStateField(data.operatingConditions.draughtAft),
        trim: initStateField(data.operatingConditions.trim),
        recommendedTrim: initStateField(data.operatingConditions.recommendedTrim),
        ballastWaterOnBoard: initStateField(data.operatingConditions.ballastWaterOnBoard),
        ballastWaterIntake: initStateField(data.operatingConditions.ballastWaterIntake),
        ballastWaterDischarged: initStateField(data.operatingConditions.ballastWaterDischarged),
        ballastWaterSampleDate: initStateField(data.operatingConditions.ballastWaterSampleDate),
        ballastWaterCountOfCells: initStateField(data.operatingConditions.ballastWaterCountOfCells),
      },
      reportTime: {
        reportFrom: initStateField(data.reportTime.reportFrom),
        reportTo: initStateField(data.reportTime.reportTo),
        eosp: initStateField(data.reportTime.eosp),
        reportCreation: initStateField(data.reportTime.reportCreation),
        timezone: initStateField(tvaTimezoneToSelectOption(data.reportTime.timezone)),
        previousTimezone: initStateField(tvaTimezoneToSelectOption(data.reportTime.previousTimezone)),
        reportPeriod: initStateField(data.reportTime.reportPeriod),
        timeSailed: initStateField(data.reportTime.timeSailed),
      },
      portAndPosition: {
        portName: initStateField(data.portAndPosition.portName),
        portPosition: initStateField(
          data.portAndPosition.portPosition
            ? {
                latDegrees: data.portAndPosition.portPosition.latDegrees,
                latMinutes: data.portAndPosition.portPosition.latMinutes,
                latSeconds: data.portAndPosition.portPosition.latSeconds,
                latDirection: data.portAndPosition.portPosition.latDirection,
                longDegrees: data.portAndPosition.portPosition.longDegrees,
                longMinutes: data.portAndPosition.portPosition.longMinutes,
                longSeconds: data.portAndPosition.portPosition.longSeconds,
                longDirection: data.portAndPosition.portPosition.longDirection,
              }
            : createEmptyLatLng()
        ),
      },

      stabilityConditions: {
        displacement: initStateField<number>(data.stabilityConditions.displacement),
        gm: initStateField<number>(data.stabilityConditions.gm),
        shearForce: initStateField<number>(data.stabilityConditions.shearForce),
        bendingForce: initStateField<number>(data.stabilityConditions.bendingForce),
        torsion: initStateField<number>(data.stabilityConditions.torsion),
        gmReserve: initStateField<number>(data.stabilityConditions.gmReserve),
        deadWeightReserve: initStateField<number>(data.stabilityConditions.deadWeightReserve),
        constantWeight: initStateField<number>(data.stabilityConditions.constantWeight),
      },
      portOperationsTimestamp: {
        etd: initStateField<Date>(data.portOperationsTimestamp.etd),
        norTendered: initStateField<Date>(data.portOperationsTimestamp.norTendered),
        allFast: initStateField<Date>(data.portOperationsTimestamp.allFast),
        hosesConnected: initStateField<Date>(data.portOperationsTimestamp.hosesConnected),
        commenceLoad: initStateField<Date>(data.portOperationsTimestamp.commenceLoad),
        finishLoad: initStateField<Date>(data.portOperationsTimestamp.finishLoad),
        hosesDisconnected: initStateField<Date>(data.portOperationsTimestamp.hosesDisconnected),
        lastLine: initStateField<Date>(data.portOperationsTimestamp.lastLine),
        cargoDocsOnboard: initStateField<Date>(data.portOperationsTimestamp.cargoDocsOnboard),
        startDischarging: initStateField<Date>(data.portOperationsTimestamp.startDischarging),
        endDischarging: initStateField<Date>(data.portOperationsTimestamp.endDischarging),
        completedTankInspection: initStateField<Date>(data.portOperationsTimestamp.completedTankInspection),
      },
      nextPort: {
        awaitingOrders: initStateField<boolean>(data.nextPort.awaitingOrders),
        nextPort: initStateField(data.nextPort.nextPort),
        remainingDistance: initStateField<number>(data.nextPort.remainingDistance),
        remainingTime: initStateField<number>(data.nextPort.remainingTime),
        nextTimezone: initStateField(tvaTimezoneToSelectOption(data?.nextPort?.nextTimezone)),
        etaPilotStation: initStateField<Date>(data.nextPort.etaPilotStation),
      },
      poolSpecificFields: {
        freshWaterDomestic: initStateField(data?.poolSpecificFields?.freshWaterDomestic),
        freshWaterTankCleaning: initStateField(data?.poolSpecificFields?.freshWaterTankCleaning),
        slopsResidualTanks: initStateField(data?.poolSpecificFields?.slopsResidualTanks),
        slopsSlopTanks: initStateField(data?.poolSpecificFields?.slopsSlopTanks),
        totalInterfaceOil: initStateField(data?.poolSpecificFields?.totalInterfaceOil),
        totalInterfaceWater: initStateField(data?.poolSpecificFields?.totalInterfaceWater),
        firstLine: initStateField(data?.poolSpecificFields?.firstLine),
        gangwayDown: initStateField(data?.poolSpecificFields?.gangwayDown),
        cargoDocsOnboard: initStateField(data?.poolSpecificFields?.cargoDocsOnboard),
      },
      otherDetails: {
        numPassengers: initStateField<number>(data.otherDetails.numPassengers),
        numCrew: initStateField<number>(data.otherDetails.numCrew),
      },
      voyageItinerary: {
        maneuveringType: initStateField(
          convertCodeToOption(data.voyageItinerary?.maneuveringType, MANEUVERING_TYPE_OPTIONS)
        ),
        awaitingOrders: initStateField<boolean>(data.voyageItinerary?.awaitingOrders),
        arrivingAtEosp: initStateField<boolean>(data.voyageItinerary?.arrivingAtEosp),
        nextPort: initStateField(data.voyageItinerary?.nextPort),
        eta: initStateField<Date>(data.voyageItinerary?.eta),
        etaTimezone: initStateField(tvaTimezoneToSelectOption(data.voyageItinerary?.etaTimezone)),
        previousEta: initStateField<Date>(data.voyageItinerary?.previousEta),
        cosp: initStateField<Date>(data.voyageItinerary?.cosp),
        previouslyInEca: initStateField<boolean>(asAnswer(data.voyageItinerary?.previouslyInEca)),
        enterEca: initStateField<Date>(data.voyageItinerary?.enterEca),
        exitEca: initStateField<Date>(data.voyageItinerary?.exitEca),
        timeInEca: initStateField<number>(data.voyageItinerary?.timeInEca),
        inEca: initStateField<boolean>(data.voyageItinerary?.inEca),
      },
      portCallReasons: {
        purposesOfCall: initStateField<string[]>(data.portCallReasons?.purposesOfCall ?? []),
      },
    },
    calculations: {} as Record<string, CalculationStatus>,
  };
};

/**
 * What a new report starts with, taken from the report before it.
 *
 * **The template now owns twenty-seven of these fields:**
 *
 * - `vesselDetails.reportNumber`, `voyageNumber`, `instructedSpeed`,
 *   `instructedFoc`, `cpSpeed` and `cpFuel`
 * - `reportTime.reportFrom`, `reportTo`, `timezone` and `previousTimezone`
 * - `voyageItinerary.nextPort`, `eta`, `etaTimezone` and `previouslyInEca`
 * - `miscellaneous.captainName`, `chiefEngineer`, `ballastCode` and
 *   `routingAdviceFollowed`
 * - `cargoDetails.cargoWeight`
 * - `nextPort.nextPort` and `nextTimezone`
 * - `operatingConditions.draughtForward`, `draughtAft` and
 *   `ballastWaterOnBoard`
 * - `stabilityConditions.constantWeight`
 * - `portAndPosition.portName` and `portPosition`
 *
 * Each holds a `carryOver` entry with `onCreate`, and `withCarryOverOnCreate`
 * writes them after this runs. Every other field here still carries from this
 * function.
 *
 * Add to that list as fields move. To move one: write its `carryOver` entry
 * into the template, empty its `initStateField` call here, and name it above.
 * **Empty the call, do not delete it.** A field the init leaves out never
 * renders, and `updateProperty` drops a write to a field it cannot find, so a
 * deleted key loses the value twice over.
 *
 * See the Carry-over section of docs/agents/patterns/form-rule-language.md.
 */
const initWithCarryOver = (data: OperationalState) => {
  return {
    formState: {
      vesselDetails: {
        // The template carries it: the previous number plus one, or 1 for a
        // vessel's first report. The power tab reads 1 as "the first report"
        // and unlocks its previous counter readings, so the template entry
        // must keep its else branch. See canEditPreviousValue.
        reportNumber: initStateField<number>(),
        // The template carries these five from the same field of the previous
        // report. Voyage Number stays empty on a Sea report after a Departure.
        voyageNumber: initStateField<string>(),
        cpSpeed: initStateField<number>(),
        cpFuel: initStateField<number>(),
        instructedSpeed: initStateField<number>(),
        instructedFoc: initStateField<number>(),
        otherSpeedSteaming: initStateField<SelectOption>(DEFAULT_OTHER_SPEED_STEAMING_OPTION),
      },
      miscellaneous: {
        // The template carries these four from the same field of the previous
        // report, as V1 does. Loading Condition reads the stored code through
        // the ballastCode option list.
        captainName: initStateField<string>(),
        chiefEngineer: initStateField<string>(),
        cargoWeight: initStateField<number>(),
        operatingCode: initStateField<SelectOption>(),
        ballastCode: initStateField<SelectOption>(),
        routingAdviceFollowed: initStateField<boolean>(),
      },
      cargoDetails: {
        // The template carries it on a report that is not a Port report. It
        // reads the previous weight from the section that held it.
        cargoWeight: initStateField<number>(),
      },
      operatingConditions: {
        operationType: initStateField<SelectOption>(),
        cargoLocation: initStateField<SelectOption>(),
        tugsAllFast: initStateField<number>(),
        tugsLastLine: initStateField<number>(),
        draughtForward: initStateField<number>(),
        draughtAft: initStateField<number>(),
        trim: initStateField<number>(),
        recommendedTrim: initStateField<number>(),
        ballastWaterOnBoard: initStateField<number>(),
        ballastWaterIntake: initStateField<number>(),
        ballastWaterDischarged: initStateField<number>(),
        ballastWaterSampleDate: initStateField<Date>(),
        ballastWaterCountOfCells: initStateField<number>(),
      },
      reportTime: {
        // The template carries reportFrom, reportTo, timezone and
        // previousTimezone. It writes them after this runs, so the value here
        // would only be overwritten. The key stays: a field the init leaves out
        // never renders, and the reducer drops a write to a field it cannot
        // find.
        reportFrom: initStateField<Date>(),
        reportTo: initStateField<Date>(),
        // A new report does not arrive at EOSP until the user says so, so EOSP
        // starts empty. It carries nothing from the report before it.
        eosp: initStateField<Date>(),
        reportCreation: initStateField(new Date()),
        timezone: initStateField<SelectOption>(),
        previousTimezone: initStateField<SelectOption>(),
        reportPeriod: initStateField<number>(),
        timeSailed: initStateField<number>(),
      },
      portAndPosition: {
        // The template carries both on a report that is not a Sea report, when
        // the previous report holds a port name, as V1 does. Position starts as
        // an empty position, not as nothing, so the field draws its six inputs
        // on a report the template writes nothing to.
        portName: initStateField<string>(),
        portPosition: initStateField(createEmptyLatLng()),
      },
      stabilityConditions: {
        displacement: initStateField<number>(),
        gm: initStateField<number>(),
        shearForce: initStateField<number>(),
        bendingForce: initStateField<number>(),
        torsion: initStateField<number>(),
        gmReserve: initStateField<number>(),
        deadWeightReserve: initStateField<number>(),
        // The template carries it from the same field of the previous report,
        // as V1 does. V1 copies none of the other seven.
        constantWeight: initStateField<number>(),
      },
      portOperationsTimestamp: {
        etd: initStateField(data.portOperationsTimestamp.etd),
        norTendered: initStateField<Date>(),
        allFast: initStateField<Date>(),
        hosesConnected: initStateField<Date>(),
        commenceLoad: initStateField<Date>(),
        finishLoad: initStateField<Date>(),
        hosesDisconnected: initStateField<Date>(),
        lastLine: initStateField<Date>(),
        cargoDocsOnboard: initStateField<Date>(),
        startDischarging: initStateField<Date>(),
        endDischarging: initStateField<Date>(),
        completedTankInspection: initStateField<Date>(),
      },
      poolSpecificFields: {
        // V1 copies none of these to a new report, and the template holds no
        // entry for them, so each one starts empty.
        freshWaterDomestic: initStateField<number>(),
        freshWaterTankCleaning: initStateField<number>(),
        slopsResidualTanks: initStateField<number>(),
        slopsSlopTanks: initStateField<number>(),
        totalInterfaceOil: initStateField<number>(),
        totalInterfaceWater: initStateField<number>(),
        firstLine: initStateField<Date>(),
        gangwayDown: initStateField<Date>(),
        cargoDocsOnboard: initStateField<Date>(),
      },
      nextPort: {
        awaitingOrders: initStateField<boolean>(),
        // The template carries nextPort and nextTimezone from the same field
        // of the previous report, except on a Port report after an
        // Anchor-class report. V1 carries no next timezone.
        nextPort: initStateField<string>(),
        remainingDistance: initStateField<number>(),
        remainingTime: initStateField<number>(),
        nextTimezone: initStateField<SelectOption>(),
        etaPilotStation: initStateField<Date>(),
      },
      otherDetails: {
        numPassengers: initStateField<number>(),
        numCrew: initStateField<number>(),
      },
      voyageItinerary: {
        // A Maneuvering report states its own kind, so the field never carries.
        maneuveringType: initStateField<SelectOption>(),
        awaitingOrders: initStateField<boolean>(),
        arrivingAtEosp: initStateField<boolean>(),
        // The template carries nextPort, eta and etaTimezone from the same
        // field of the previous report, and previouslyInEca from the previous
        // inEca. It writes them after this runs, so the key stays and holds no
        // value.
        nextPort: initStateField<string>(),
        eta: initStateField<Date>(),
        etaTimezone: initStateField<SelectOption>(),
        // Not a value the user sees: the 24-hour ETA warning compares against
        // it, and the section draws no field for it. So the store keeps it.
        previousEta: initStateField<Date>(data.voyageItinerary?.eta),
        cosp: initStateField<Date>(),
        previouslyInEca: initStateField<boolean>(),
        enterEca: initStateField<Date>(),
        exitEca: initStateField<Date>(),
        timeInEca: initStateField<number>(),
        inEca: initStateField<boolean>(),
      },
      portCallReasons: {
        purposesOfCall: initStateField<string[]>([]),
      },
    },
    calculations: {} as Record<string, CalculationStatus>,
  };
};

export const OperationalStore = signalStore(
  { providedIn: 'root' },
  withDevtools('OperationalStore'),
  withState(initBlank()),
  withPortCallPurposesReducer(),
  withOperationalEffects(),
  withFormStoreReducer(operationalEvents, initOperationalStateReducer, 'operational'),
  withCalculationReducer(
    'displacement',
    operationalCalculationEvents.getDisplacement,
    operationalCalculationEvents.getDisplacementSuccess,
    operationalCalculationEvents.getDisplacementFailed
  ),
  withComputed((store) => {
    const vesselSpecStore = inject(VesselSpecStore);
    const vesselParticularsStore = inject(VesselParticularsStore);

    return {
      vm: computed(() =>
        buildOperationalVm(
          store.formState(),
          vesselSpecStore.vesselSpec(),
          vesselParticularsStore.vesselParticulars(),
          store.portCallPurposes()
        )
      ),
    };
  }),
  withFeature((store) => withCalculatedValues(operationalEvents, () => collectOperationalCalculatedValues(store.vm())))
);
